import React from 'react';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';

import { Language } from '../../utils/language';
import { number, GetInterface, Codec, string, Either, Left, Right } from 'purify-ts';
import * as R from 'remeda';
import { updateValue, Field, touchField } from '../../components/formTypes';
import { RemoteData } from '../../utils/remoteData';
import { Omraader } from './types';

const Fylke = Codec.interface({
  navn: string,
  nummer: number,
});

type Fylke = GetInterface<typeof Fylke>;

interface FylkeSelectMultipleProps {
  selectedFylker: Field<Fylke[]>;
  omraader: RemoteData<Error, Omraader>;
  updateSelectedFylker: React.Dispatch<Field<Fylke[]>>;
  language?: Language;
  variant?: 'filled' | 'standard' | 'outlined' | undefined;
}

function getFylkeFromName(fylker: Fylke[], fylkeNames: string[]): Either<string, Fylke[]> {
  const fylkeSearch = fylkeNames.map(fylkeName => fylker.find(fylke => fylke.navn === fylkeName));

  const filteredFylkeSearch = R.compact(fylkeSearch);

  return fylkeSearch ? Right(filteredFylkeSearch) : Left('Kunne ikke finne fylke');
}

export function toggleSelectedFylke(selectedFylker: Fylke[], selectedFylke: Fylke): Fylke[] {
  const fylkeSearch = R.find(selectedFylker, f => f === selectedFylke);
  if (fylkeSearch === undefined) {
    return R.pipe(
      selectedFylker,
      R.concat([selectedFylke]),
      R.uniqBy(f => f.nummer),
    );
  } else {
    return R.pipe(
      selectedFylker,
      R.filter(f => f === fylkeSearch),
      R.uniqBy(f => f.nummer),
    );
  }
}

export default function SharedFylkeMultiNew({
  omraader,
  selectedFylker,
  updateSelectedFylker,
  language = 'nb_NO',
  variant = 'filled',
}: FylkeSelectMultipleProps) {
  return (
    <FormControl variant={variant} style={{ width: '100%' }}>
      <Autocomplete
        disabled={omraader.type !== 'SUCCESS'}
        closeText="Lukk"
        multiple
        disableCloseOnSelect
        autoHighlight
        value={R.map(selectedFylker.value, f => f.navn)}
        onBlur={() => updateSelectedFylker(touchField(selectedFylker))}
        options={
          omraader.type === 'SUCCESS'
            ? R.pipe(
                omraader.data.fylker,
                R.map(fylke => fylke.navn),
                R.sortBy(R.identity),
              )
            : ([] as string[])
        }
        onChange={(_, value) => {
          if (omraader.type === 'SUCCESS') {
            getFylkeFromName(omraader.data.fylker, value).caseOf({
              Left: e => console.error(`Problemer med å sette fylker : ${e}`),
              Right: f => updateSelectedFylker(updateValue(selectedFylker, f)),
            });
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={language === 'nb_NO' ? 'Velg fylke' : 'Vel fylke'}
            variant={variant}
            fullWidth
          />
        )}
      />
    </FormControl>
  );
}
